import { useDispatch, useSelector } from "react-redux";
import "./App.css";
import { Route, Routes, Navigate } from "react-router";
import React, { useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { initialize } from "./store/initializeSlice";
import { sessionExpired, fetchTenantBilling, setBilling } from "./store/authSlice";
import { detectPortalFromPath } from "./utils/portalTypes";
import {
  UNIFIED_FORGOT_PASSWORD_PATH,
  UNIFIED_LOGIN_PATH,
} from "./utils/authRedirect";
import {
  consumePortalTokenFromHash,
  getPlatformUrl,
  isClinicPortal,
  redirectToClinicPortal,
} from "./utils/portalHost";
import { PortalProvider, usePortal } from "./context/PortalContext";
import WithRole from "./components/hoc/withRole";
import Preloader from "./components/common/Preloader";
import ErrorBoundary from "./components/common/ErrorBoundary";
import { ClientSplashStatic } from "./components/Clinic/PatientPortal/ClientSplash";
import { AdminSplashStatic } from "./components/Clinic/AdminPortal/AdminSplash";
import services from "./services";
import { usePwaEntryRedirect } from "./hooks/useStandalone";
import { usePwaManifestBranding } from "./hooks/usePwaManifestBranding";
import { startTokenRefreshLoop } from "./utils/authTokenUtils";
import { hideBootSplash, isPortalAppPath } from "./utils/bootSplash";

import HomeLanding from "./components/Clinic/HomeLanding";
import CleanJournalLanding from "./components/Clinic/CleanAdLanding";
import YandexTrainerLanding from "./components/Clinic/YandexStyleLanding";
import AvitoCrmLanding from "./components/Clinic/AvitoStyleLanding";
import CreateAppLanding from "./components/Clinic/CreateAppLanding";
import ClinicFinder from "./components/Clinic/ClinicFinder";
import ClinicNotFound from "./components/Clinic/ClinicNotFound";
import PortalEntryRedirect from "./components/Clinic/PortalEntryRedirect";
import ClinicRegister from "./components/Clinic/ClinicRegister";
import ClinicCreatedSuccess from "./components/Clinic/ClinicCreatedSuccess";
import Seo from "./components/Seo";
import PatientLogin from "./components/Clinic/PatientLogin";
import PatientRegister from "./components/Clinic/PatientRegister";
import PatientForgotPassword from "./components/Clinic/PatientForgotPassword";
import PatientResetPassword from "./components/Clinic/PatientResetPassword";
import PatientPortal from "./components/Clinic/PatientPortal";
import AdminDashboard from "./components/Clinic/AdminDashboard";
import StaffTrainerPortal from "./components/Clinic/StaffPortal/StaffTrainerPortal";
import PatientBookPage from "./components/Clinic/PatientBookPage";
import ExternalRedirect from "./components/Clinic/ExternalRedirect";
import PlatformAdmin from "./components/Platform/PlatformAdmin";
import PlatformDashboard from "./components/Platform/PlatformAdmin/PlatformDashboard";
import PlatformFunnel from "./components/Platform/PlatformAdmin/PlatformFunnel";
import PlatformAuditLog from "./components/Platform/PlatformAdmin/PlatformAuditLog";
import PlatformPlans from "./components/Platform/PlatformAdmin/PlatformPlans";
import PlatformTenants from "./components/Platform/PlatformAdmin/PlatformTenants";
import PlatformLeads from "./components/Platform/PlatformAdmin/PlatformLeads";
import PlatformOnboardingEmails from "./components/Platform/PlatformAdmin/PlatformOnboardingEmails";
import PlatformTenantDetail from "./components/Platform/PlatformAdmin/PlatformTenantDetail";
import OwnerCabinet from "./components/Platform/OwnerCabinet";
import PlatformSupport from "./components/Platform/PlatformAdmin/PlatformSupport";
import PlatformMore from "./components/Platform/PlatformAdmin/PlatformMore";
import PlatformPayments from "./components/Platform/PlatformAdmin/PlatformPayments";
import PlatformNotificationsPage from "./components/Platform/PlatformAdmin/PlatformNotifications";
import YandexMetrikaTracker from "./components/YandexMetrikaTracker";
import CookieBanner from "./components/Legal/CookieBanner";
import UserLegalGate from "./components/Legal/UserLegalGate";
import ClientConsentGate from "./components/Legal/ClientConsentGate";
import LegalPage from "./components/Legal/LegalPage";

if (typeof window !== "undefined" && isClinicPortal()) {
  consumePortalTokenFromHash();
}

function PlatformAdminTenantLegacyRedirect() {
  const { id } = useParams();
  return <Navigate to={`/admin/tenants/${id}`} replace />;
}

function PortalGate({ children }) {
  const init = useSelector((state) => state.init.initialized);
  const { isPortal, loading, notFound } = usePortal();
  const { pathname } = useLocation();
  const isClientApp = pathname.startsWith("/app");
  const isAdminApp = pathname.startsWith("/manage/app");
  const isStaffApp = pathname.startsWith("/staff/app");
  const ready = init && !(isPortal && loading);

  useEffect(() => {
    if (ready || isPortalAppPath(pathname)) {
      hideBootSplash();
    }
  }, [ready, pathname]);

  const loader = isClientApp ? (
    <ClientSplashStatic />
  ) : isAdminApp || isStaffApp ? (
    <AdminSplashStatic />
  ) : (
    <Preloader />
  );

  if (!init) {
    /* На платформе HTML boot-splash уже крутится — второй экран не дублируем */
    if (!isClientApp && !isAdminApp && !isStaffApp) return null;
    return loader;
  }
  if (isPortal && loading) return loader;
  if (isPortal && notFound) return <ClinicNotFound />;
  return (
    <>
      {isPortal ? <Seo noindex title="Baysport" /> : null}
      {children}
    </>
  );
}

function ClinicRoutes() {
  return (
    <Routes>
      {/* ——— Портал клиента ——— */}
      <Route path="/" element={<PortalEntryRedirect />} />
      <Route path="/book" element={<PatientBookPage />} />
      <Route path="/embed/book" element={<PatientBookPage embedOnly />} />
      <Route path="/login" element={<PatientLogin />} />
      <Route path="/register" element={<PatientRegister />} />
      <Route path="/legal/:docSlug" element={<LegalPage />} />
      <Route path="/forgot-password" element={<PatientForgotPassword />} />
      <Route path="/reset-password" element={<PatientResetPassword />} />
      <Route
        path="/app/*"
        element={
          <WithRole roles={["client"]} loginPath="/login">
            <PatientPortal />
          </WithRole>
        }
      />

      {/* ——— Портал персонала ——— */}
      <Route path="/staff" element={<PortalEntryRedirect />} />
      <Route path="/staff/login" element={<Navigate to={UNIFIED_LOGIN_PATH} replace />} />
      <Route
        path="/staff/forgot-password"
        element={<Navigate to={UNIFIED_FORGOT_PASSWORD_PATH} replace />}
      />
      <Route
        path="/staff/app/*"
        element={
          <WithRole roles={["trainer", "owner"]} loginPath={UNIFIED_LOGIN_PATH}>
            <StaffTrainerPortal />
          </WithRole>
        }
      />

      {/* ——— Портал администратора ——— */}
      <Route path="/manage" element={<PortalEntryRedirect />} />
      <Route path="/manage/login" element={<Navigate to={UNIFIED_LOGIN_PATH} replace />} />
      <Route
        path="/manage/forgot-password"
        element={<Navigate to={UNIFIED_FORGOT_PASSWORD_PATH} replace />}
      />
      <Route
        path="/manage/app/*"
        element={
          <WithRole roles={["admin", "owner"]} loginPath={UNIFIED_LOGIN_PATH}>
            <AdminDashboard />
          </WithRole>
        }
      />

      <Route
        path="/register-clinic"
        element={
          <ExternalRedirect href={getPlatformUrl("/register-clinic")} />
        }
      />
      <Route
        path="/admin/*"
        element={<ExternalRedirect href={getPlatformUrl("/admin")} />}
      />

      {/* Старые URL → новые */}
      <Route path="/auth/patient" element={<Navigate to="/login" replace />} />
      <Route path="/auth/patient/register" element={<Navigate to="/register" replace />} />
      <Route path="/auth/doctor" element={<Navigate to="/login" replace />} />
      <Route path="/auth/admin" element={<Navigate to="/login" replace />} />
      <Route path="/auth" element={<Navigate to="/login" replace />} />
      <Route path="/clinic/patient" element={<Navigate to="/app" replace />} />
      <Route path="/clinic/patient/visits" element={<Navigate to="/app/visits" replace />} />
      <Route path="/clinic/doctor" element={<Navigate to="/staff/app" replace />} />
      <Route path="/clinic/doctor/schedule" element={<Navigate to="/staff/app/schedule" replace />} />
      <Route path="/clinic/admin/*" element={<Navigate to="/manage/app" replace />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function PlatformRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomeLanding />} />
      <Route path="/sozdat-prilozhenie-dlya-kluba" element={<CreateAppLanding />} />
      <Route
        path="/sozdat-prilozenie"
        element={<Navigate to="/sozdat-prilozhenie-dlya-kluba" replace />}
      />
      <Route path="/crm-dlya-sportivnogo-kluba" element={<AvitoCrmLanding />} />
      <Route path="/zhurnal-poseshchaemosti" element={<CleanJournalLanding />} />
      <Route path="/dlya-trenera" element={<YandexTrainerLanding />} />
      <Route
        path="/dlya-edinoborstv"
        element={<Navigate to="/dlya-trenera" replace />}
      />
      <Route path="/find" element={<ClinicFinder />} />
      <Route path="/book" element={<Navigate to="/find" replace />} />
      <Route path="/register-clinic" element={<ClinicRegister />} />
      <Route path="/register-clinic/success" element={<ClinicCreatedSuccess />} />
      <Route path="/legal/:docSlug" element={<LegalPage />} />
      <Route path="/account/*" element={<OwnerCabinet />} />
      <Route path="/admin" element={<PlatformAdmin />}>
        <Route index element={<PlatformDashboard />} />
        <Route path="funnel" element={<PlatformFunnel />} />
        <Route path="analytics" element={<Navigate to="/admin/funnel" replace />} />
        <Route path="more" element={<PlatformMore />} />
        <Route path="audit" element={<PlatformAuditLog />} />
        <Route path="plans" element={<PlatformPlans />} />
        <Route path="payments" element={<PlatformPayments />} />
        <Route path="notifications" element={<PlatformNotificationsPage />} />
        <Route path="tenants" element={<PlatformTenants />} />
        <Route path="tenants/:id" element={<PlatformTenantDetail />} />
        <Route path="leads" element={<PlatformLeads />} />
        <Route path="onboarding-emails" element={<PlatformOnboardingEmails />} />
        <Route path="support/*" element={<PlatformSupport />} />
      </Route>
      <Route path="/platform/clinics" element={<Navigate to="/admin/tenants" replace />} />
      <Route path="/platform/admin" element={<Navigate to="/admin" replace />} />
      <Route path="/platform/admin/plans" element={<Navigate to="/admin/plans" replace />} />
      <Route path="/platform/admin/tenants" element={<Navigate to="/admin/tenants" replace />} />
      <Route
        path="/platform/admin/tenants/:id"
        element={<PlatformAdminTenantLegacyRedirect />}
      />
      <Route path="/auth/*" element={<Navigate to="/find" replace />} />
      <Route path="/clinic/*" element={<Navigate to="/find" replace />} />
      <Route path="/catalog/*" element={<Navigate to="/" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function RegistrationLinkRedirect() {
  const location = useLocation();

  useEffect(() => {
    if (isClinicPortal()) return;
    const params = new URLSearchParams(location.search);
    const link = params.get("link");
    const subdomain = params.get("subdomain");
    if (!link || !subdomain) return;
    redirectToClinicPortal(
      subdomain,
      `/register?link=${encodeURIComponent(link)}`,
    );
  }, [location.search]);

  return null;
}

function InviteResetRedirect() {
  const location = useLocation();

  useEffect(() => {
    if (isClinicPortal()) return;
    const path = location.pathname.replace(/\/$/, "") || "/";
    if (path !== "/reset-password") return;

    const params = new URLSearchParams(location.search);
    const subdomain = (params.get("subdomain") || "").trim().toLowerCase();
    const token = params.get("token");
    if (!subdomain || !token) return;

    params.delete("subdomain");
    const qs = params.toString();
    redirectToClinicPortal(subdomain, `/reset-password${qs ? `?${qs}` : ""}`);
  }, [location.pathname, location.search]);

  return null;
}

function AppRoutes() {
  return isClinicPortal() ? <ClinicRoutes /> : <PlatformRoutes />;
}

function PwaManifestBranding() {
  const location = useLocation();
  usePwaManifestBranding(location.pathname);
  return null;
}

function App() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const initialized = useSelector((state) => state.init.initialized);
  const isAuth = useSelector((state) => state.auth.isAuth);
  const role = useSelector((state) => state.auth.role);

  useEffect(() => {
    dispatch(initialize());
  }, [dispatch]);

  usePwaEntryRedirect(initialized, isAuth, role, location.pathname, navigate);

  useEffect(() => {
    const timerId = startTokenRefreshLoop();
    return () => window.clearInterval(timerId);
  }, []);

  useEffect(() => {
    services.setUnauthorizedHandler(() => {
      dispatch(sessionExpired());
      if (location.pathname.startsWith("/admin")) {
        navigate("/admin");
        return;
      }
      if (location.pathname.startsWith("/account")) {
        navigate("/account/login");
        return;
      }
      const login = detectPortalFromPath(location.pathname).login;
      if (!location.pathname.startsWith(login)) {
        navigate(login);
      }
    });
    services.setSubscriptionRequiredHandler((payload) => {
      if (payload?.billing) {
        dispatch(setBilling(payload.billing));
      } else {
        dispatch(fetchTenantBilling());
      }
    });
  }, [dispatch, navigate, location.pathname]);

  return (
    <ErrorBoundary>
      <PortalProvider>
        <PwaManifestBranding />
        <PortalGate>
          <RegistrationLinkRedirect />
          <InviteResetRedirect />
          <YandexMetrikaTracker />
          <CookieBanner />
          <UserLegalGate />
          <ClientConsentGate />
          <div className="App">
            <AppRoutes />
          </div>
        </PortalGate>
      </PortalProvider>
    </ErrorBoundary>
  );
}

export default App;
